import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, Star, Globe, Shield, CheckCircle,
  MapPin, ChevronDown, ChevronRight, ChevronLeft,
  Menu, X, Facebook, Instagram, Twitter, Users
} from "lucide-react";
import SearchBar from "../../components/SearchBar";
import Navbar from "../../components/Header";

import App from "../data/App.jpg";

import { useFavorites } from "../features/useFavorites";

import MoheliImg   from "../data/moheli.jpeg";

/* ─────────────────────────────────────────────────
   COULEURS
───────────────────────────────────────────────── */
const G       = "#3A9D23";
const G_DARK  = "#2f7d1c";
const G_LIGHT = "#e8f5e2";
const API     = "https://api.komorotel.com";

/* ─────────────────────────────────────────────────
   DONNÉES STATIQUES
───────────────────────────────────────────────── */
const NAV_LINKS = [
  { label: "Hébergements", to: "/hotels"       },
  { label: "Destinations",  to: "/destinations" },
  { label: "Offres",        to: "/offres"       },
  { label: "À propos",      to: "/about"        },
  { label: "Contact",       to: "/contact"      },
];

const ISLANDS = [
  { name: "Ngazidja", slug: "Ngazidja", image: "https://www.vanilla-islands.org/wp-content/uploads/2015/12/accueil-comores-plage-barque.jpg" },
  { name: "Anjouan",                   slug: "Anjouan",  image: "https://www.nomade-aventure.com/Content/Images/ImgProduits/COM/11221/701448_QuatreTiers.ori.jpg"  },
  { name: "Mohéli",                    slug: "Moheli",   image: MoheliImg  },
];
const WHY_ITEMS = [
  { icon: <MapPin size={24} color={G} />,      title: "100% Comores",          desc: "Une plateforme locale dédiée à notre archipel." },
  { icon: <Users size={24} color={G} />,       title: "Soutien local",         desc: "Nous valorisons les hébergements et l'économie locale." },
  { icon: <Shield size={24} color={G} />,      title: "Réservation sécurisée", desc: "Paiement sécurisé et confirmation immédiate." },
  { icon: <CheckCircle size={24} color={G} />, title: "Hébergements vérifiés", desc: "Tous nos hébergements sont contrôlés et approuvés." },
];

const FOOTER_LINKS = {
  Explorer:     ["Hébergements", "Destinations", "Offres spéciales"],
  Informations: ["À propos", "FAQ", "Conditions générales"],
  Aide:         ["Contact", "Politique de confidentialité", "Mentions légales"],
};

/* ─────────────────────────────────────────────────
   LOGO SVG
───────────────────────────────────────────────── */
function KomorotelLogo({ size = 38, light = false }) {
  const c = light ? "#fff" : G;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
      <circle cx="40" cy="40" r="38" fill={light ? "rgba(255,255,255,0.15)" : `${G}18`} />
      <path d="M22 55 Q15 40 22 27 Q30 15 42 14" stroke={c} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M22 55 Q28 63 38 64"              stroke={c} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q50 68 54 57"              stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q35 58 40 54 Q45 50 42 44" stroke={c} strokeWidth="2"   strokeLinecap="round" fill="none"/>
      <rect x="39" y="12" width="7" height="48" rx="1.5" fill={c}/>
      <path d="M46 40 L64 24 L64 27 L52 40 L64 56 L61 56 L46 42 Z" fill={c}/>
      <circle cx="28" cy="28" r="2.5" fill={c}/>
      <circle cx="25" cy="36" r="2.5" fill={c}/>
      <circle cx="24" cy="44" r="2.5" fill={c}/>
      <circle cx="25" cy="52" r="2.5" fill={c}/>
    </svg>
  );
}



/* ─────────────────────────────────────────────────
   HERO
───────────────────────────────────────────────── */

function HeroSection() {
  return (
    <section style={{
      position: "relative",
      minHeight: "clamp(580px, 80vh, 680px)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%",
    }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${App})`,
        backgroundSize: "cover",
        backgroundPosition: "65% 70%", 
      }} />
      {/* Overlay gradient */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to right, rgba(10,40,15,0.92) 0%, rgba(10,40,15,0.75) 40%, rgba(10,40,15,0.25) 75%, transparent 100%)",
      }} />
 
      {/* Contenu */}
      <div
        className="hero-content"
        style={{
          overflow: "visible",
          position: "relative", zIndex: 2,
          maxWidth: 1280, margin: "0 auto", width: "100%",
          padding: "120px 32px 80px",
          boxSizing: "border-box",
        }}>
 
        {/* Badge */}
        <div className="hero-badge" style={{
          display: "inline-flex", alignItems: "center",
          background: G, color: "#fff",
          fontSize: 11, fontWeight: 800, letterSpacing: "0.09em",
          padding: "5px 14px", borderRadius: 20, marginBottom: 16,
        }}>
          <span style={{ marginRight: 6 }}>🇰🇲</span>BIENVENUE AUX COMORES
        </div>
 
        <h1 style={{
          fontFamily: "Georgia, serif",
          fontSize: 52, fontWeight: 800, color: "#fff",
          lineHeight: 1.1, marginBottom: 12, letterSpacing: "-0.01em",
        }}>
          Explorez les<br />Comores autrement
        </h1>
 
        <p style={{
          fontSize: 15, color: "rgba(255,255,255,0.82)",
          lineHeight: 1.65, marginBottom: 28, fontWeight: 400,
        }}>
          Des hébergements authentiques sur chaque île de l'archipel.
        </p>
 
        <SearchBar />
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   SECTION ÎLES
───────────────────────────────────────────────── */
function IslandsSection() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    fetch(`${API}/api/hotels/countByIsland?islands=Ngazidja,Anjouan,Moheli`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setCounts({ Ngazidja: d[0]||0, Anjouan: d[1]||0, Moheli: d[2]||0 }); })
      .catch(() => {});
  }, []);

  return (
    <section style={{ background: "#fff", padding: "64px 0" }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: 8 }}>Choisissez votre île</h2>
      <div style={{ width: 40, height: 3, background: G, borderRadius: 2, margin: "0 auto 40px" }} />
      <div className="islands-grid" style={{ maxWidth: 1280, margin: "0 auto", padding: "0 16px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        {ISLANDS.map(island => (
          <IslandCard key={island.slug} island={{ ...island, count: counts[island.slug] }} navigate={navigate} />
        ))}
      </div>
    </section>
  );
}

function IslandCard({ island, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", height: 260, cursor: "pointer" }}
      onClick={() => navigate(`/hotels?destination=${island.slug}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <img src={island.image} alt={island.name}
        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.45s ease", transform: hovered ? "scale(1.07)" : "scale(1)" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.02) 100%)" }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "22px 24px" }}>
        <h3 style={{ fontFamily: "Georgia, serif", fontSize: 21, fontWeight: 700, color: "#fff", marginBottom: 4, lineHeight: 1.25 }}>
          {island.name.split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}
        </h3>
        {island.count > 0 && (
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", marginBottom: 14, fontWeight: 500 }}>
            {island.count} hébergement{island.count > 1 ? "s" : ""}
          </div>
        )}
        <button style={{
          background: "rgba(255,255,255,0.15)", color: "#fff",
          border: "1.5px solid rgba(255,255,255,0.5)", borderRadius: 8,
          backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
          padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
          transition: "background 0.2s, border-color 0.2s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.3)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.9)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)"; }}>
          Découvrir les hébergements →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   HÉBERGEMENTS POPULAIRES
───────────────────────────────────────────────── */
function PopularSection() {
  const navigate  = useNavigate();
  const scrollRef = useRef(null);
  const [hotels,   setHotels]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/hotels?limit=8`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          // Garde uniquement ceux qui ont une photo ET un prix
          const valid = data.filter(h => h.photos?.[0] && h.minPrice);
          setHotels(valid.slice(0, 4));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanLeft(scrollLeft > 4);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    el?.addEventListener("scroll", checkScroll);
    checkScroll();
    return () => el?.removeEventListener("scroll", checkScroll);
  }, [hotels]);

  return (
    <section style={{ background: "#fff", padding: "8px 0 56px" }}>
      {/* Header */}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, color: "#111827" }}>Hébergements populaires</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate("/hotels")} style={{ background: "none", border: "none", cursor: "pointer", color: G, fontSize: 13, fontWeight: 700 }}>Voir tout</button>
          {[{ dir: -1, can: canLeft }, { dir: 1, can: canRight }].map(({ dir, can }, i) => (
            <button key={i} onClick={() => scrollRef.current?.scrollBy({ left: dir * 270, behavior: "smooth" })}
              disabled={!can}
              style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #e5e7eb", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: can ? "pointer" : "not-allowed", opacity: can ? 1 : 0.35, color: "#374151" }}>
              {dir < 0 ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
            </button>
          ))}
        </div>
      </div>

      {/* Scroll */}
      <div ref={scrollRef} style={{ maxWidth: 1280, margin: "0 auto", padding: "0 16px 4px", display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
        {loading
          ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
          : hotels.map(h => <HotelCard key={h._id} hotel={h} navigate={navigate} />)
        }
      </div>
    </section>
  );
}

function HotelCard({ hotel, navigate }) {
  const [hovered, setHovered] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const liked = isFavorite(hotel._id);

  return (
    <div style={{ flex: "0 0 255px", borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid #efefef", cursor: "pointer", scrollSnapAlign: "start", transition: "box-shadow 0.2s, transform 0.2s", boxShadow: hovered ? "0 8px 28px rgba(0,0,0,0.11)" : "0 2px 8px rgba(0,0,0,0.06)", transform: hovered ? "translateY(-3px)" : "none" }}
      onClick={() => hotel.slug ? navigate(`/hotel/slug/${hotel.slug}`) : navigate(`/hotels/${hotel._id}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      {/* Image */}
      <div style={{ position: "relative", height: 170, overflow: "hidden" }}>
        <img src={hotel.photos[0]} alt={hotel.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.45s ease", transform: hovered ? "scale(1.07)" : "scale(1)" }} />

        {/* Gradient bas */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.28) 0%, transparent 50%)" }} />

        {/* Favori — conservé mais sans logique backend pour l'instant */}
        <button onClick={() => toggleFavorite(hotel._id)}
          style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.9)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", transition: "transform 0.15s" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
          <Heart size={13} fill={liked ? "#ef4444" : "none"} color={liked ? "#ef4444" : "#6b7280"} />
        </button>

        {/* Prix en bas à gauche sur l'image */}
        <div style={{ position: "absolute", bottom: 10, left: 10, background: "rgba(0,0,0,0.52)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", color: "#fff", padding: "4px 10px", borderRadius: 7 }}>
          <div style={{ fontSize: 9, fontWeight: 500, opacity: 0.75, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 1 }}>À partir de</div>
          <div style={{ fontSize: 13, fontWeight: 800 }}>{hotel.minPrice}€<span style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}> /nuit</span></div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "13px 15px 15px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{hotel.name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <MapPin size={11} color="#9ca3af" />
          <span style={{ fontSize: 11, color: "#9ca3af" }}>{hotel.city || hotel.island}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Note — affichée seulement si elle existe */}
          {hotel.rating ? (
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={11} fill="#f59e0b" color="#f59e0b" />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{hotel.rating}</span>
            </div>
          ) : (
            <span style={{ fontSize: 11, color: "#d1d5db" }}>Nouveau</span>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: G }}>Voir →</span>
        </div>
      </div>
    </div>
  );
}

/* Skeleton */
function SkeletonCard() {
  return (
    <div style={{ flex: "0 0 255px", borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid #efefef" }}>
      <div style={{ height: 170, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
      <div style={{ padding: "13px 15px" }}>
        <div style={{ height: 13, background: "#f3f4f6", borderRadius: 6, marginBottom: 8, width: "65%" }} />
        <div style={{ height: 11, background: "#f3f4f6", borderRadius: 6, width: "45%" }} />
      </div>
      <style>{`@keyframes shimmer{to{background-position:-200% 0}}`}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   POURQUOI KOMOROTEL
───────────────────────────────────────────────── */
function WhySection() {
  return (
    <section style={{ background: "#f9fafb", padding: "64px 0" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: 48 }}>Pourquoi choisir Komorotel ?</h2>
        <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24 }}>
          {WHY_ITEMS.map((item, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 16, padding: "28px 22px", textAlign: "center", border: "1px solid #f3f4f6", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                {item.icon}
              </div>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{item.title}</h4>
              <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.7 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   CTA HÔTELIERS
───────────────────────────────────────────────── */
function HotelierCTA() {
  const navigate = useNavigate();
  return (
    <section style={{ background: "#1a4a2e", padding: "40px 0" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/>
            </svg>
          </div>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 5 }}>Vous êtes hôtelier ? Rejoignez Komorotel</h3>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.72)" }}>Développez votre visibilité et accueillez plus de voyageurs.</p>
          </div>
        </div>
        <button onClick={() => navigate("/register-hotel")}
          style={{ background: "#fff", color: "#1a4a2e", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", transition: "opacity 0.18s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
          Proposer mon établissement →
        </button>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────────── */
function Footer() {
  const [email, setEmail] = useState("");
  return (
    <footer style={{ background: "#122010", padding: "52px 0 24px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>
        <div className="footer-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr 1fr 1.5fr", gap: 32, marginBottom: 44 }}>

          {/* Brand */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <KomorotelLogo size={34} light />
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, letterSpacing: "0.04em" }}>KOMOROTEL</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>Réservez local, voyagez authentique.</div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginBottom: 18 }}>
              La première plateforme de réservation d'hébergements 100% dédiée aux Comores.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {[<Facebook size={14}/>, <Instagram size={14}/>, <Twitter size={14}/>].map((icon, i) => (
                <a key={i} href="#" style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.6)", textDecoration: "none", transition: "all 0.18s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.color = G; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}>
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Colonnes liens */}
          {Object.entries(FOOTER_LINKS).map(([cat, links]) => (
            <div key={cat}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: "#fff", marginBottom: 16 }}>{cat}</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {links.map(l => (
                  <li key={l} style={{ marginBottom: 9 }}>
                    <a href="#" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none", fontSize: 13, transition: "color 0.18s", display: "block" }}
                      onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.6)"}>
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: "#fff", marginBottom: 10 }}>Recevez nos meilleures offres</div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginBottom: 14 }}>Des séjours exclusifs, des bons plans et l'actualité des Comores.</p>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="email" placeholder="Votre email" value={email} onChange={e => setEmail(e.target.value)}
                style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#fff", outline: "none", fontFamily: "inherit" }} />
              <button style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.18s" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                S'abonner
              </button>
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
          <span>© 2025 Komorotel. Tous droits réservés.</span>
          <span>Made with ♥ aux Comores</span>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────── */
export default function HomePage() {
  return (
    <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}>
      <style>{mobileCSS}</style>
      <Navbar />
      <HeroSection />
      <IslandsSection />
      <PopularSection />
      <WhySection />
      <HotelierCTA />
      <Footer />
    </div>
  );
}

/* ── CSS GLOBAL MOBILE ── */
const mobileCSS = `
  @media (max-width: 768px) {
    .hero-content {
      padding: 100px 16px 48px !important;
      justify-content: flex-start;
    }
    .hero-content h1 {
      font-size: 32px !important;
      margin-bottom: 8px !important;
    }
    .hero-content p {
      font-size: 14px !important;
      margin-bottom: 20px !important;
    }
    /* Badge plus compact */
    .hero-badge {
      font-size: 10px !important;
      padding: 4px 10px !important;
      margin-bottom: 12px !important;
    }
    .islands-grid {
      grid-template-columns: 1fr !important;
      gap: 14px !important;
      padding: 0 16px !important;
    }
    .why-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    .footer-grid {
      grid-template-columns: 1fr 1fr !important;
    }
  }
  @media (max-width: 480px) {
    .why-grid {
      grid-template-columns: 1fr !important;
    }
    .footer-grid {
      grid-template-columns: 1fr !important;
    }
  }
`;